import { Box, Typography, Checkbox, Autocomplete, Chip, TextField, FormControlLabel } from "@mui/material";
import { Controller } from "react-hook-form";
import isEmpty from "lodash/isEmpty";

import Input from "../../components/UserInput/Input";
import { SingleSelectInput } from "../../components/SelectInput";
import { colors } from "../../theme/colors";
import { specimenStorage } from "../../utils/constants";
import style from "./testType.module.css";
import type { TestTypeFormFieldsProps, SpecialityOption } from "./testTypeTypes";

/**
 * Renders the shared form fields for Test Type add/edit flows.
 *
 * Args:
 *   register: React Hook Form register function for field bindings.
 *   errors: Field errors keyed by form field name.
 *   control: React Hook Form control instance for controlled fields.
 *   getValues: Getter for current form values.
 *   watch: Watcher for reactive form values.
 *   setValue: Setter for programmatic form value updates.
 *   specialities: List of speciality options for multi-select mapping.
 *
 * Returns:
 *   JSX.Element: The shared Test Type form fields.
 */
const TestTypeFormFields = ({
  register,
  errors,
  control,
  getValues,
  watch,
  setValue,
  specialities,
}: TestTypeFormFieldsProps) => {
  const isIntraOperativeSelected = watch("is_intra_operative") ?? false;

  return (
    <>
      <Box className={style.row}>
        <Input
          placeholder="e.g Histopathology"
          label="Name"
          required
          register={register}
          name="name"
          errors={errors}
        />
        <SingleSelectInput
          placeholder="Select storage"
          label="Storage Type"
          required
          register={register}
          name="specimen_storage"
          errors={errors}
          data={specimenStorage ?? []}
          value={watch("specimen_storage") ?? ""}
        />
      </Box>
      <Box className={style.row}>
        <Input
          placeholder="Enter information message"
          label="Information"
          register={register}
          name="info"
          errors={errors}
        />
      </Box>
      <Box className={style.row} sx={{ flexDirection: "column" }}>
        <Typography
          className={style.inputLabel}
          sx={{ marginBottom: -2, display: "flex" }}
        >
          Specialities Mapping
          <Typography sx={{ color: "red", marginLeft: 0.7 }}>
            {" *"}
          </Typography>
        </Typography>
        <Controller
          control={control}
          name="speciality_ids"
          rules={{ required: true }}
          render={({ field: { onChange, value }, ...props }) => (
            <Autocomplete<SpecialityOption, true, true, false>
              {...props}
              sx={{
                width: "100%",
              }}
              multiple
              disablePortal
              disableCloseOnSelect
              id="tags-outlined"
              options={specialities ?? []}
              disableClearable={true}
              onChange={(e, data) => onChange(data)}
              value={value ?? []}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, optionValue) => option.id === optionValue.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    isEmpty(getValues("speciality_ids"))
                      ? "Select specialities"
                      : ""
                  }
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                  error={!!errors.speciality_ids}
                  helperText={
                    errors.speciality_ids
                      ? "Speciality is required"
                      : ""
                  }
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              )}
            />
          )}
        />
      </Box>
      <Box className={style.row} sx={{ marginTop: "20px" }}>
        <Box sx={{ marginLeft: 1 }}>
          <FormControlLabel
            sx={{ "& span": { py: 0, px: 0.3 } }}
            control={
              <Checkbox
                {...register("is_intra_operative")}
                sx={{ color: colors.grey }}
                onChange={() => {
                  setValue("is_intra_operative", !isIntraOperativeSelected);
                }}
                checked={isIntraOperativeSelected}
              />
            }
            label="Intra Operative Test"
          />
        </Box>
      </Box>
    </>
  );
};

export default TestTypeFormFields;
